import type { ActionKind } from "./types";

/** Operations share a pause only when they perform the same Instagram action. */
export const IG_OPERATION_LABELS: Record<string, string> = {
  follow: "Following", unfollow: "Unfollowing", like: "Likes",
  comment: "Comments", story_view: "Story views", send_dm: "Sending DMs", publish: "Publishing",
  edit_post: "Caption edits", profiles: "Profile reads", posts: "Profile post lists", media: "Post details",
  comments: "Reading comments", stories: "Reading stories", relationships: "Relationship checks",
  followers: "Follower lists", following: "Following lists", likers: "Post liker lists",
  inbox: "Inbox sync", thread: "Conversation reads", search: "Search", locations: "Location posts",
  insights: "Insights", suggestions: "Suggested accounts", legacy: "Previous operation",
};

export function igOperationLabel(operation: string): string {
  return IG_OPERATION_LABELS[operation] ?? "Instagram request";
}

export function actionOperation(kind: ActionKind): string {
  return kind === "dm_new" || kind === "dm_reply" ? "send_dm" : kind;
}

/** Both transports/fallbacks for the same action use the same key. Unknown endpoints stay isolated. */
export function requestOperation(key: string, access: "read" | "write" | "publish"): string {
  if (access === "publish") return "publish";
  if (access === "write") {
    if (/Unfollow|friendships\/destroy/.test(key)) return "unfollow";
    if (/FollowMutation|friendships\/create/.test(key)) return "follow";
    if (/CommentActions.*Like/.test(key)) return "like";
    if (/LikeMedia|LikePost|\/likes\//.test(key)) return "like";
    if (/CommentCreate|\/comments\/.*\/add/.test(key)) return "comment";
    if (/SeenMutation/.test(key)) return "story_view";
    if (/DirectTextSend|direct_v2\/.*broadcast/.test(key)) return "send_dm";
    if (/edit_media/.test(key)) return "edit_post";
  } else {
    if (/ProfilePosts|ProfileReels|feed\/user/.test(key)) return "posts";
    if (/PostRoot|media_info|\/media\/[^/]+\/info/.test(key)) return "media";
    if (/comments|CommentsContainer/.test(key)) return "comments";
    if (/followers/.test(key)) return "followers";
    if (/following/.test(key)) return "following";
    if (/likers|LikedByList/.test(key)) return "likers";
    if (/show_many|friendships\/.*show/.test(key)) return "relationships";
    if (/ProfilePage|HoverCard|web_profile_info|route-definition/.test(key)) return "profiles";
    if (/Stories|reels_media/.test(key)) return "stories";
    if (/ThreadDetail|MessageList/.test(key)) return "thread";
    if (/Inbox|MessageRequest|ThreadList|UnreadThreadCount|direct_v2\/inbox/.test(key)) return "inbox";
    if (/Search|Topsearch/.test(key)) return "search";
    if (/LocationPage/.test(key)) return "locations";
    if (/insights/i.test(key)) return "insights";
    if (/suggest|discover\/ayml/i.test(key)) return "suggestions";
  }
  return `${access}:${key}`.slice(0, 160);
}
